import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { pickBy } from 'lodash'
import { useSelector } from 'react-redux'
import { Box, Stack, IconButton, Tooltip } from '@mui/material_modern'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useGetDispatchTicketsQuery } from 'services/dispatch'
import { useGetDriversQuery } from 'services/drivers'
import DispatchActiveTicketsLoadBoard from 'components/containers/load-board/components/DispatchActiveTicketsLoadBoard'
import DispatchActiveTicketsDateSelector from 'components/containers/load-board/components/DispatchActiveTicketsDateSelector'
import DispatchActiveTicketsSummary from 'components/containers/load-board/components/DispatchActiveTicketsSummary'
import Loading from 'components/generic/Loading'
import { DISPATCH_TICKET_STATUSES, DISPATCH_TICKET_TYPES, FILTER_OPTION_TYPES } from 'utils/constants'
import { useSearchBarV2 } from 'utils/custom-hooks/useSearchBarV2'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { getISOLocaleMidnightTime } from 'utils/data-manipulation/valueFormatter'
import { addToDate, objectDetailsToDropdownFormatter } from 'utils/data-manipulation/common'
import { dispatchSnackbar, isDateToday } from 'utils/helpers'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Button } from '@mui/material'

const boxStyles = {
  border: 1,
  margin: 1,
  borderColor: 'grey.400',
  borderRadius: 2,
  backgroundColor: 'background.default',
}

const DispatchActiveTicketsContainer = () => {
  const { showPastLoadBoard } = useSelector(state => state.settings?.settings?.dispatchSettings?.loadBoard || {})
  const { sideBarWidth, inOrgMode } = useSelector(state => state.ui)
  const { yardDetailsList, user } = useSelector(state => state.auth)

  const [selectedDate, setSelectedDate] = useState(new Date())
  const [showRequestedTickets, setShowRequestedTickets] = useState(true)

  const requestedTicketsFilter = {
    search: '',
    scheduleDateFrom: null,
    scheduleDateTo: null,
    pastTicketsEnabled: false,
    statuses: [DISPATCH_TICKET_STATUSES.REQUESTED.id],
  }

  const defaultFilter = useMemo(
    () => ({
      search: '',
      scheduleDateFrom: getISOLocaleMidnightTime(selectedDate),
      scheduleDateTo: getISOLocaleMidnightTime(addToDate(selectedDate, 1)),
      // Only show prior tickets if the selected date is the default
      pastTicketsEnabled: showPastLoadBoard && isDateToday(selectedDate),
      statuses: [
        DISPATCH_TICKET_STATUSES.UNASSIGNED.id,
        DISPATCH_TICKET_STATUSES.ASSIGNED.id,
        DISPATCH_TICKET_STATUSES.IN_PROGRESS.id,
        DISPATCH_TICKET_STATUSES.COMPLETED.id,
        DISPATCH_TICKET_STATUSES.FAILED.id,
      ],
      yardIds: null,
    }),
    [selectedDate, showPastLoadBoard],
  )

  const filterStorageString = useMemo(() => {
    if (!user?.attributes?.sub) return
    return 'loadBoardFilter:' + user?.attributes?.sub
  }, [user])

  const storedFilter = useMemo(() => {
    if (!filterStorageString) return

    const storedFilterString = window.localStorage.getItem(filterStorageString)
    return storedFilterString && storedFilterString !== 'undefined' ? JSON.parse(storedFilterString) : {}
  }, [filterStorageString])

  const startingFilter = useMemo(
    () => ({
      ...defaultFilter,
      driverIds: storedFilter?.driverIds || defaultFilter.driverIds,
      types: storedFilter?.types || defaultFilter.types,
      statuses: storedFilter?.statuses === null ? defaultFilter.statuses : storedFilter?.statuses,
      yardIds: inOrgMode ? storedFilter?.yardIds || defaultFilter.yardIds : null,
    }),
    [selectedDate, showPastLoadBoard, storedFilter, inOrgMode],
  )

  const onReset = () => {
    window.localStorage.setItem(filterStorageString, JSON.stringify(defaultFilter))
  }

  const [filter, setFilter] = useState(startingFilter)

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      scheduleDateFrom: getISOLocaleMidnightTime(selectedDate),
      scheduleDateTo: getISOLocaleMidnightTime(addToDate(selectedDate, 1)),
      pastTicketsEnabled: showPastLoadBoard && isDateToday(selectedDate),
    }))
  }, [selectedDate, showPastLoadBoard])

  const {
    data: dispatchTicketsData,
    isFetching: isFetchingDispatchTicketsData,
    refetch,
  } = useGetDispatchTicketsQuery({
    filter: JSON.stringify({
      ...defaultFilter,
      ...pickBy(filter, value => {
        // Remove null/undefined values AND empty arrays
        return value != null && !(Array.isArray(value) && value.length === 0)
      }),
    }),
  })

  const {
    data: requestedDispatchTicketsData,
    isFetching: isFetchingRequestedDispatchTicketsData,
    requestedRefetch,
  } = useGetDispatchTicketsQuery({
    filter: JSON.stringify(pickBy(requestedTicketsFilter)), // use pickBy to remove null filters
  })

  const { data: driversData, isFetching: isFetchingDriversData } = useGetDriversQuery({
    filter: JSON.stringify({ isActive: true }),
  })

  const isLoading = isFetchingDispatchTicketsData || isFetchingDriversData || isFetchingRequestedDispatchTicketsData

  useGlobalLoader(false)

  const additionalFilterOptions = [
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'driverIds',
      label: 'Driver',
      options: driversData?.Items || [],
      defaultValue: startingFilter?.driverIds,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          driverIds: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    },
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'types',
      label: 'Ticket Type',
      options: [
        DISPATCH_TICKET_TYPES.PICK_UP,
        DISPATCH_TICKET_TYPES.DROP_OFF,
        DISPATCH_TICKET_TYPES.EXCHANGE,
        DISPATCH_TICKET_TYPES.PICK_AND_RETURN,
        DISPATCH_TICKET_TYPES.DELIVERY,
        DISPATCH_TICKET_TYPES.LIVE_LOAD,
      ],
      defaultValue: startingFilter?.types,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          types: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    },
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'statuses',
      label: 'Ticket Status',
      options: [
        DISPATCH_TICKET_STATUSES.ASSIGNED,
        DISPATCH_TICKET_STATUSES.UNASSIGNED,
        DISPATCH_TICKET_STATUSES.IN_PROGRESS,
        DISPATCH_TICKET_STATUSES.FAILED,
        DISPATCH_TICKET_STATUSES.COMPLETED,
        DISPATCH_TICKET_STATUSES.VOID,
      ],
      defaultValue: startingFilter?.statuses,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          statuses: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    },
  ]

  // If in org mode, we add in the option to filter by yard
  if (inOrgMode) {
    additionalFilterOptions.unshift({
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'yardIds',
      label: 'Yards',
      options: yardDetailsList?.map((item, index) => objectDetailsToDropdownFormatter(item, index)) || [],
      defaultValue: startingFilter?.yardIds,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          yardIds: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    })
  }

  const dispatchTickets = useMemo(() => {
    return dispatchTicketsData?.Items?.slice() || []
  }, [dispatchTicketsData])

  const requestedDispatchTickets = useMemo(() => {
    return requestedDispatchTicketsData?.Items?.slice() || []
  }, [requestedDispatchTicketsData])

  const { filteredRows, additionalFilters, renderSearchBar } = useSearchBarV2({
    textSearchPlaceholder: 'Search for anything',
    rows: dispatchTickets,
    renderViewModeToggle: () => null,
    renderDateRangePicker: () => null,
    renderSelectFilter: () => null,
    useBackendSortAndFilter: true,
    filter,
    defaultFilter,
    setFilter,
    additionalFilterOptions,
    onReset,
    showFilterChips: true,
    filterChips:
      requestedDispatchTickets && requestedDispatchTickets.length > 0
        ? [
            <Button size="small" onClick={() => setShowRequestedTickets(!showRequestedTickets)}>
              {showRequestedTickets ? 'Hide Requests' : 'Show Requests'}
            </Button>,
          ]
        : null,
  })

  const filteredDrivers = additionalFilters?.driverIds
    ? driversData?.Items?.filter(driver => additionalFilters.driverIds.includes(driver.id)) || []
    : driversData?.Items || []

  const lastActivityTime = useRef(Date.now())

  const handleRefresh = useCallback(() => {
    refetch()
    lastActivityTime.current = Date.now()
  }, [refetch, requestedRefetch])

  const handleUserActivity = useCallback(() => {
    const now = Date.now()
    if (now - lastActivityTime.current > 15000) {
      // 15 seconds
      handleRefresh()
    }
    lastActivityTime.current = now
  }, [handleRefresh])

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    events.forEach(event => document.addEventListener(event, handleUserActivity))

    return () => {
      events.forEach(event => document.removeEventListener(event, handleUserActivity))
    }
  }, [handleUserActivity])

  return (
    <Stack
      sx={{
        height: '100%',
        backgroundColor: 'background.section',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ ...boxStyles, mb: 0.5, p: 2, flexShrink: 0 }}>{renderSearchBar()}</Box>
      <Stack
        direction="column"
        sx={{
          ...boxStyles,
          mt: 0.5,
          p: 2,
          width: `calc(100vw - ${sideBarWidth + 16}px)`,
          position: 'relative',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Loading
          localized
          localizedSx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            zIndex: 4,
            backgroundColor: 'background.loading',
            visibility: isLoading ? 'visible' : 'hidden',
            transition: isLoading ? 'visibility 0s 500ms' : 'visibility 0s',
          }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0, p: 1 }}>
          <DispatchActiveTicketsDateSelector selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={isFetchingDispatchTicketsData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <DispatchActiveTicketsSummary
              dispatchTickets={filteredRows}
              requestedDispatchTickets={requestedDispatchTickets}
            />
          </Stack>
        </Stack>
        <Stack
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 1,
          }}
        >
          <DispatchActiveTicketsLoadBoard
            dispatchTickets={filteredRows}
            requestedDispatchTickets={requestedDispatchTickets}
            drivers={filteredDrivers}
            selectedDate={selectedDate}
            showRequestedTickets={showRequestedTickets}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default DispatchActiveTicketsContainer
